import { Controller } from 'react-hook-form';
import { findCountryByValue } from '../../lib/countries/countries.utils'
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import TextArea from "../../components/form/input/TextArea";

import type { UseFormRegister, FieldValues, Control, Path } from "react-hook-form";
import type { MenuItemLang } from "../../types/menuItemTypes";

const MAX_LENGTH_DESC = 400

type MenuItemLangForm<T extends FieldValues> = {
  id: string;
  menuItemLangs: MenuItemLang[];
  register: UseFormRegister<T>;
  control: Control<T>
}

const MenuItemLangForm = <T extends FieldValues>({
  id,
  menuItemLangs,
  register,
  control
}: MenuItemLangForm<T>) => {
  return (
    <div id={id} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {menuItemLangs.map((language, index) => {
        const country = findCountryByValue(language.language)
        return (
            <div key={`${language.language}-${index}`}
              className="p-5 border border-gray-300 rounded-2xl dark:border-gray-700 lg:p-6"
            >   
              <div className="mb-4 flex flex-col items-center text-base text-center font-medium text-gray-800 dark:text-white/90 w-full">
                <img
                  alt={`${language.language}`}
                  src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${language.language.toUpperCase()}.svg`}
                  className="inline h-8 rounded-sm mb-2"
                />
                <span className="break-words max-w-full">{country?.title}</span>
              </div>
              <div>
                <input
                  {...register(`menuItemLangs.${index}.language` as Path<T>)}
                  id={`menuItemLangs.${index}.language`}
                  hidden
                  value={language.language}
                  disabled
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="name">Name</Label>
                <Controller
                  name={`menuItemLangs.${index}.name` as Path<T>}
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field, fieldState: { error }}) => (
                    <div>
                      <Input 
                        id={`menuItemLangs.${index}.name`}
                        placeholder='Enter name'
                        error={Boolean(error)}
                        {...field}
                      />
                      {error && <span className='text-sm text-error-500'>{error.message}</span>}
                    </div>
                  )}
                />  
              </div>
              <div>
                <Label>Description</Label>
                <Controller
                  name={`menuItemLangs.${index}.description` as Path<T>}
                  control={control}
                  rules={{
                    maxLength: {
                      value: MAX_LENGTH_DESC,
                      message: `Description must be less than ${MAX_LENGTH_DESC} characters.`
                    }
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <TextArea
                        {...field}
                        id={`menuItemLangs.${index}.description`}
                        rows={6}
                        placeholder='Enter description'
                        maxLength={MAX_LENGTH_DESC}
                      />
                      <small className='text-gray-800 dark:text-white/90'>{(field.value?.length || 0)} / {MAX_LENGTH_DESC}</small>
                      {error && <span className='text-sm text-error-500'>{error.message}</span>}     
                    </div>  
                  )}
                />
              </div>
            </div>
        )
      })}  
    </div>
  )
}

export default MenuItemLangForm
